import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  CircularProgress,
  Alert,
  Box,
  LinearProgress,
  Chip,
  Divider
} from '@mui/material';
import { 
  HealthAndSafety,
  TrendingUp,
  TrendingDown,
  MonetizationOn,
  AccountBalance,
  Star,
  StarHalf,
  StarBorder
} from '@mui/icons-material';
import { analyticsApi } from '../../services/api';

interface HealthMetrics {
  savingsRate: number;
  spendingConsistency: number;
  incomeStability: number;
  budgetAdherence: number;
  overallScore: number;
  scoreCategory: 'excellent' | 'good' | 'fair' | 'needs_improvement';
  recommendations: string[];
}

const FinancialHealthScore: React.FC = () => {
  const [data, setData] = useState<HealthMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get multiple data sources for comprehensive health analysis
        const [cashFlowData, budgetData, categoryComparison] = await Promise.all([
          analyticsApi.getMonthlyCashFlow(6),
          analyticsApi.getBudgetVsActual(),
          analyticsApi.getCategoryComparison()
        ]);

        // Calculate savings rate (last 3 months average)
        const recentMonths = cashFlowData.data.slice(-3);
        const avgIncome = recentMonths.reduce((sum: number, month: any) => sum + month.income, 0) / recentMonths.length;
        const avgExpenses = recentMonths.reduce((sum: number, month: any) => sum + month.expenses, 0) / recentMonths.length;
        const savingsRate = avgIncome > 0 ? ((avgIncome - avgExpenses) / avgIncome) * 100 : 0;

        // Calculate spending consistency (lower variance = better)
        const expenseAmounts = cashFlowData.data.map((month: any) => month.expenses);
        const avgExpense = expenseAmounts.reduce((sum: number, exp: number) => sum + exp, 0) / expenseAmounts.length;
        const variance = expenseAmounts.reduce((sum: number, exp: number) => sum + Math.pow(exp - avgExpense, 2), 0) / expenseAmounts.length;
        const standardDeviation = Math.sqrt(variance);
        const spendingConsistency = Math.max(0, 100 - (standardDeviation / avgExpense) * 100);

        // Calculate income stability (coefficient of variation)
        const incomeAmounts = cashFlowData.data.map((month: any) => month.income);
        const avgIncomeAll = incomeAmounts.reduce((sum: number, inc: number) => sum + inc, 0) / incomeAmounts.length;
        const incomeVariance = incomeAmounts.reduce((sum: number, inc: number) => sum + Math.pow(inc - avgIncomeAll, 2), 0) / incomeAmounts.length;
        const incomeStdDev = Math.sqrt(incomeVariance);
        const incomeStability = Math.max(0, 100 - (incomeStdDev / avgIncomeAll) * 100);

        // Calculate budget adherence
        let budgetAdherence = 75; // Default if no budget data
        if (budgetData.data && budgetData.data.length > 0) {
          const totalBudget = budgetData.total_budget;
          const totalActual = budgetData.total_actual;
          if (totalBudget > 0) {
            const adherenceRatio = Math.min(totalActual / totalBudget, 1.5); // Cap at 150%
            budgetAdherence = Math.max(0, 100 - Math.abs(1 - adherenceRatio) * 100);
          }
        }

        // Calculate overall score (weighted average)
        const overallScore = Math.round(
          savingsRate * 0.3 + 
          spendingConsistency * 0.25 + 
          incomeStability * 0.25 + 
          budgetAdherence * 0.2
        );

        // Determine score category
        let scoreCategory: 'excellent' | 'good' | 'fair' | 'needs_improvement';
        if (overallScore >= 80) scoreCategory = 'excellent';
        else if (overallScore >= 65) scoreCategory = 'good';
        else if (overallScore >= 50) scoreCategory = 'fair';
        else scoreCategory = 'needs_improvement';

        // Generate recommendations
        const recommendations: string[] = [];
        if (savingsRate < 10) recommendations.push('Increase your savings rate to at least 10%');
        if (spendingConsistency < 70) recommendations.push('Work on more consistent monthly spending');
        if (incomeStability < 70) recommendations.push('Consider diversifying income sources');
        if (budgetAdherence < 80) recommendations.push('Improve budget tracking and adherence');
        if (recommendations.length === 0) recommendations.push('Great job! Keep maintaining healthy financial habits');

        setData({
          savingsRate: Math.max(0, Math.min(100, savingsRate)),
          spendingConsistency: Math.max(0, Math.min(100, spendingConsistency)),
          incomeStability: Math.max(0, Math.min(100, incomeStability)),
          budgetAdherence: Math.max(0, Math.min(100, budgetAdherence)),
          overallScore,
          scoreCategory,
          recommendations
        });
        
        setError(null);
      } catch (err) {
        setError('Failed to load financial health data');
        console.error('Error fetching financial health:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getScoreColor = (category: string) => {
    switch (category) {
      case 'excellent': return 'success';
      case 'good': return 'info';
      case 'fair': return 'warning';
      case 'needs_improvement': return 'error';
      default: return 'primary';
    }
  };

  const getScoreLabel = (category: string) => {
    switch (category) {
      case 'excellent': return 'Excellent';
      case 'good': return 'Good';
      case 'fair': return 'Fair';
      case 'needs_improvement': return 'Needs Work';
      default: return 'Unknown';
    }
  };

  const renderStars = (score: number) => {
    const stars = [];
    const fullStars = Math.floor(score / 20);
    const hasHalfStar = (score % 20) >= 10;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Star key={i} sx={{ color: 'gold', fontSize: 20 }} />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<StarHalf key={i} sx={{ color: 'gold', fontSize: 20 }} />);
      } else {
        stars.push(<StarBorder key={i} sx={{ color: 'grey.400', fontSize: 20 }} />);
      }
    }
    return stars;
  };

  const MetricBar = ({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {icon}
          <Typography variant="body2" sx={{ ml: 1 }}>
            {label}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
          {Math.round(value)}%
        </Typography>
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={value} 
        sx={{ 
          height: 6, 
          borderRadius: 3,
          '& .MuiLinearProgress-bar': {
            backgroundColor: value >= 80 ? 'success.main' : 
                           value >= 60 ? 'info.main' : 
                           value >= 40 ? 'warning.main' : 'error.main'
          }
        }}
      />
    </Box>
  );

  if (loading) {
    return (
      <Card sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="error">{error}</Alert>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="info">No financial health data available</Alert>
      </Card>
    );
  }

  return (
    <Card sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <HealthAndSafety sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          Financial Health Score
        </Typography>
      </Box>

      {/* Overall Score */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '2rem', md: '3.75rem' } }}>
          {data.overallScore}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          {renderStars(data.overallScore)}
        </Box>
        <Chip 
          label={getScoreLabel(data.scoreCategory)}
          color={getScoreColor(data.scoreCategory) as any}
          sx={{ fontWeight: 'bold' }}
        />
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Individual Metrics */}
      <Box sx={{ mb: 2 }}>
        <MetricBar 
          label="Savings Rate" 
          value={data.savingsRate} 
          icon={<MonetizationOn sx={{ fontSize: 16, color: 'success.main' }} />}
        />
        <MetricBar 
          label="Spending Consistency" 
          value={data.spendingConsistency} 
          icon={<TrendingUp sx={{ fontSize: 16, color: 'info.main' }} />}
        />
        <MetricBar 
          label="Income Stability" 
          value={data.incomeStability} 
          icon={<AccountBalance sx={{ fontSize: 16, color: 'primary.main' }} />}
        />
        <MetricBar 
          label="Budget Adherence" 
          value={data.budgetAdherence} 
          icon={<TrendingDown sx={{ fontSize: 16, color: 'warning.main' }} />}
        />
      </Box>

      {/* Top Recommendation */}
      {data.recommendations.length > 0 && (
        <Alert 
          severity={data.scoreCategory === 'excellent' ? 'success' : 'info'} 
          sx={{ fontSize: '0.75rem', p: 1 }}
        >
          <Typography variant="caption">
            💡 {data.recommendations[0]}
          </Typography>
        </Alert>
      )}
    </Card>
  );
};

export default FinancialHealthScore;